import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePendingPosts } from '../hooks/usePendingPosts';
import Toast from './Toast';

const seenKey = () => `postNotificationsSeenAt:${localStorage.getItem('userId') || 'admin'}`;

const readSeenAt = () => {
  try {
    const stored = localStorage.getItem(seenKey());
    if (stored) return Number(stored) || 0;
    // First visit on this browser: only posts that arrive from now on count as new
    const now = Date.now();
    localStorage.setItem(seenKey(), String(now));
    return now;
  } catch {
    return Date.now();
  }
};

const writeSeenAt = (time) => {
  try {
    localStorage.setItem(seenKey(), String(time));
  } catch {
    // storage unavailable: the badge simply comes back on the next visit
  }
};

const authorName = (post) => `${post.firstName || ''} ${post.lastName || ''}`.trim() || 'A user';

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * Bell for the admin dashboard: tells staff when users post. New posts arrive as "pending", so the
 * bell watches the pending list, badges the ones created since the staff member last looked, and
 * pops a toast when one arrives while the dashboard is open.
 */
const NotificationBell = ({ pollIntervalMs }) => {
  const navigate = useNavigate();
  const signedIn = !!localStorage.getItem('accessToken');
  const { data } = usePendingPosts({ enabled: signedIn, intervalMs: pollIntervalMs });

  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [seenAt, setSeenAt] = useState(readSeenAt);
  const knownIdsRef = useRef(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const unread = items.filter((post) => new Date(post.createdAt).getTime() > seenAt);

  // Toast for posts that show up while the dashboard is open (not for the backlog at page load)
  useEffect(() => {
    if (!data) return;
    const ids = new Set(items.map((post) => post.postId));
    if (knownIdsRef.current) {
      const arrived = items.filter((post) => !knownIdsRef.current.has(post.postId));
      if (arrived.length > 0) {
        const newest = arrived[0];
        setToast({
          message:
            arrived.length === 1
              ? `New post from ${authorName(newest)}: ${newest.title || 'Untitled'}`
              : `${arrived.length} new posts are waiting for review`,
          type: 'success',
        });
      }
    }
    knownIdsRef.current = ids;
  }, [data, items]);

  const toggle = () => {
    if (!open) {
      const latest = items.reduce((max, post) => Math.max(max, new Date(post.createdAt).getTime() || 0), 0);
      const now = Math.max(Date.now(), latest);
      writeSeenAt(now);
      setSeenAt(now);
    }
    setOpen((value) => !value);
  };

  const openPending = () => {
    setOpen(false);
    navigate('/posts?status=pending');
  };

  if (!signedIn) return null;

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={`Notifications${unread.length ? `, ${unread.length} new` : ''}`}
        title="New posts"
        style={{
          position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
          padding: 6, marginLeft: 'auto', display: 'flex', color: '#1F1010',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread.length > 0 && (
          <span
            data-testid="notification-badge"
            style={{
              position: 'absolute', top: 0, right: 0, minWidth: 18, height: 18, borderRadius: 9,
              background: '#e53935', color: '#fff', fontSize: 11, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
            }}
          >
            {unread.length}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="New posts"
          style={{
            position: 'fixed', top: 16, left: 292, width: 340, maxHeight: '70vh', overflowY: 'auto',
            background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            zIndex: 1100, fontFamily: 'Poppins, sans-serif', color: '#1F1010',
          }}
        >
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', fontWeight: 600, fontSize: 14 }}>
            Posts awaiting review{data ? ` (${data.totalCount})` : ''}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: '#666' }}>No new posts.</div>
          ) : (
            items.slice(0, 5).map((post) => {
              const isNew = new Date(post.createdAt).getTime() > seenAt;
              return (
                <button
                  key={post.postId}
                  type="button"
                  onClick={openPending}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                    padding: '10px 16px', background: isNew ? '#f1f8e9' : '#fff',
                    borderBottom: '1px solid #f3f3f3', fontFamily: 'inherit',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{authorName(post)} posted</div>
                  <div style={{ fontSize: 13, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {post.title || post.content || 'Untitled'}
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>{timeAgo(post.createdAt)}</div>
                </button>
              );
            })
          )}
          <button
            type="button"
            onClick={openPending}
            style={{
              display: 'block', width: '100%', padding: 12, border: 'none', background: '#fafafa',
              cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#2e7d32', fontFamily: 'inherit',
            }}
          >
            Review pending posts
          </button>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} duration={6000} onClose={() => setToast(null)} />}
    </>
  );
};

export default NotificationBell;
